import { useEffect, useState } from 'react';

import { adminApi } from '../adminApi';
import { formatDate, formatInt } from '../format';

// Usage → Rejected telemetry (ADR 0149). The telemetry "evolution signal":
// path families boats actually send that the family policy rejects at ingest,
// aggregated fleet-wide. Every row is a candidate family for
// packages/telemetry — the battery capacity.stateOfCharge bug sat invisible
// in exactly this data before this page existed. One row per family, not per
// instance (TOM-280, ADR 0297): `*` stands for any instance, with one real
// example path and how many instances the busiest boat sends. Read-only
// observability, so it lives with the Usage pages.

interface RejectedFamilyRow {
  family: string;
  example: string;
  boats: number;
  instances: number;
  unit: string | null;
  firstSeen: string;
  lastSeen: string;
}

interface RejectedPathsSummary {
  families: RejectedFamilyRow[];
  overflow: { boats: number; rejectedDropped: number };
}

export function AdminRejectedPaths() {
  const [data, setData] = useState<RejectedPathsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    adminApi<RejectedPathsSummary>('/v1/admin/telemetry/rejected-paths')
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load rejected telemetry paths.');
      });
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Rejected telemetry</h1>
        {data !== null && (
          <span className="admin-count">{formatInt(data.families.length)} families</span>
        )}
      </header>
      <p className="admin-hint">
        Path families boats send that the telemetry policy rejects at ingest, with each instance
        segment shown as <code>*</code>. Widespread rows are real hardware signals worth supporting
        — each one is a candidate family for the shared telemetry schema. Paths refused on purpose
        are not listed.
      </p>
      {error !== null && <p className="admin-error admin-auth-banner error">{error}</p>}
      {data === null && error === null && <p className="admin-loading-row">Loading…</p>}
      {data !== null && data.families.length === 0 && (
        <p className="admin-empty">No rejected paths. Every path the fleet sends is accepted.</p>
      )}
      {data !== null && data.families.length > 0 && (
        <div className="scroll-x">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Family</th>
                <th>Example</th>
                <th>Boats</th>
                <th>Instances</th>
                <th>Reported unit</th>
                <th>First seen</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.families.map((row) => (
                <tr key={row.family}>
                  <td>
                    <code>{row.family}</code>
                  </td>
                  <td>
                    <code>{row.example}</code>
                  </td>
                  <td>{formatInt(row.boats)}</td>
                  <td>{formatInt(row.instances)}</td>
                  <td>{row.unit ?? '—'}</td>
                  <td>{formatDate(row.firstSeen)}</td>
                  <td>{formatDate(row.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data !== null && data.overflow.rejectedDropped > 0 && (
        <p className="admin-hint">
          {formatInt(data.overflow.boats)} boat(s) hit the per-boat rejected-family cap;{' '}
          {formatInt(data.overflow.rejectedDropped)} were counted but not cataloged (distinct paths
          before 2026-10, families since).
        </p>
      )}
    </section>
  );
}
